import React, {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  GripHorizontal,
  GripVertical,
  Maximize2,
  Minimize2,
} from "lucide-react";
import "./attorneyWorkspace.css";

type Panel = "record" | "work";
export type WorkTab = "answer" | "messages";
export type AttorneyWorkspaceHandle = { showMessages: () => void };
type Layout = { columns: number; documentRows: number };
const defaults: Layout = { columns: 45, documentRows: 55 };
const limits = { columns: [30, 65], documentRows: [35, 70] };
const storageKey = "reviewed.attorney-workspace.layout.v3";
const clamp = (key: keyof Layout, value: number) =>
  Math.min(limits[key][1], Math.max(limits[key][0], value));

function readLayout(): Layout {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || "{}");
    return Object.fromEntries(
      Object.entries(defaults).map(([key, value]) => [
        key,
        typeof stored?.[key] === "number" && Number.isFinite(stored[key])
          ? clamp(key as keyof Layout, stored[key])
          : value,
      ])
    ) as Layout;
  } catch {
    return defaults;
  }
}

export function Divider({
  name,
  value,
  axis,
  range,
  controls,
  onChange,
}: {
  name: string;
  value: number;
  axis: "x" | "y";
  range: number[];
  controls: string;
  onChange: (value: number) => void;
}) {
  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    const bounds = e.currentTarget.parentElement!.getBoundingClientRect();
    const length = axis === "x" ? bounds.width : bounds.height;
    const position =
      axis === "x" ? e.clientX - bounds.left : e.clientY - bounds.top;
    if (length > 10) onChange(((position - 5) / (length - 10)) * 100);
  };
  return (
    <div
      className={`attorney-divider attorney-divider-${axis}`}
      role="separator"
      tabIndex={0}
      aria-label={name}
      aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      aria-valuemin={range[0]}
      aria-valuemax={range[1]}
      aria-valuenow={Math.round(value)}
      aria-valuetext={`${Math.round(value)} percent`}
      aria-controls={controls}
      title="Drag to resize, or use the arrow keys"
      onPointerDown={e => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.focus();
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e);
      }}
      onPointerMove={e => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) move(e);
      }}
      onPointerUp={e => {
        if (e.currentTarget.hasPointerCapture(e.pointerId))
          e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onKeyDown={e => {
        const decrease = axis === "x" ? "ArrowLeft" : "ArrowUp";
        const increase = axis === "x" ? "ArrowRight" : "ArrowDown";
        if (![decrease, increase, "Home", "End"].includes(e.key)) return;
        e.preventDefault();
        onChange(
          e.key === "Home"
            ? range[0]
            : e.key === "End"
              ? range[1]
              : value + (e.key === increase ? 2 : -2)
        );
      }}
    >
      {axis === "x" ? <GripVertical size={12} /> : <GripHorizontal size={12} />}
    </div>
  );
}

/**
 * The attorney's desk: the client's materials beside one working panel with
 * Answer and Messages tabs. Reviews with documents stack the materials on top
 * so the document viewer keeps its full width.
 */
const AttorneyWorkspace = forwardRef<
  AttorneyWorkspaceHandle,
  {
    record: React.ReactNode;
    discussion: React.ReactNode;
    draft: React.ReactNode;
    answers: React.ReactNode;
    hasDocuments: boolean;
    answerCount: number;
    tab: WorkTab;
    onTabChange: (tab: WorkTab) => void;
    messageCount: number;
    clientWaiting: boolean;
    hasDraft: boolean;
    canRevise?: boolean;
    closed?: boolean;
  }
>(function AttorneyWorkspace(
  {
    record,
    discussion,
    draft,
    answers,
    hasDocuments,
    answerCount,
    tab,
    onTabChange,
    messageCount,
    clientWaiting,
    hasDraft,
    canRevise = true,
    closed = false,
  },
  ref
) {
  const [layout, setLayout] = useState(readLayout);
  const [focus, setFocus] = useState<Panel | null>(null);
  const prefix = useId();
  // After delivery the delivered answer leads; a revision is opt-in unless one is underway.
  const [revising, setRevising] = useState(hasDraft);
  const deliveredCount = useRef(answerCount);
  useEffect(() => {
    if (answerCount > deliveredCount.current) setRevising(false);
    deliveredCount.current = answerCount;
  }, [answerCount]);
  const workspace = useRef<HTMLDivElement>(null);
  const positions = useRef<
    { element: HTMLElement; top: number; left: number }[]
  >([]);
  function focusPanel(next: Panel | null) {
    if (!focus && next) {
      positions.current = Array.from(
        workspace.current!.querySelectorAll<HTMLElement>(
          ".attorney-panel-content, .review-messages, .attorney-draft-editor, .submitted-conversation-scroll, .submitted-document-content, .attorney-answer-history-content"
        )
      ).map(element => ({
        element,
        top: element.scrollTop,
        left: element.scrollLeft,
      }));
    }
    setFocus(next);
  }
  useImperativeHandle(ref, () => ({
    showMessages() {
      onTabChange("messages");
      focusPanel("work");
      // Wait for the selected pane and focused layout to render. Only a
      // deliberate action moves focus; opening a review never does.
      requestAnimationFrame(() => {
        const composer = workspace.current?.querySelector<HTMLElement>(
          ".review-composer textarea"
        );
        const destination =
          composer ||
          workspace.current?.querySelector<HTMLElement>(
            '[aria-label="Discussion messages"]'
          );
        destination?.scrollIntoView({ block: "center" });
        destination?.focus({ preventScroll: true });
      });
    },
  }));
  useLayoutEffect(() => {
    if (focus) return;
    // Enlarging a pane can clamp its scroll offset. Restore the original desk view.
    for (const { element, top, left } of positions.current) {
      element.scrollTop = top;
      element.scrollLeft = left;
    }
    positions.current = [];
  }, [focus]);
  function resize(key: keyof Layout, value: number) {
    const next = { ...layout, [key]: clamp(key, value) };
    setLayout(next);
    // Only panel dimensions are stored; review content never enters localStorage.
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* optional preference */
    }
  }
  const expandButton = (key: Panel, title: string) => (
    <button
      type="button"
      className="attorney-panel-expand"
      aria-label={
        focus === key ? `Restore all sections from ${title}` : `Expand ${title}`
      }
      aria-expanded={focus === key}
      title={
        focus === key ? "Restore all sections (Escape)" : `Expand ${title}`
      }
      onClick={() => focusPanel(focus === key ? null : key)}
    >
      {focus === key ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
      <span>{focus === key ? "Restore" : "Expand"}</span>
    </button>
  );
  const showDraft = !closed && (answerCount === 0 || (revising && canRevise));
  const answerTab = (
    <div className="attorney-answer-combined">
      {showDraft ? (
        <>
          <div className="attorney-private-heading">
            <h3>
              {answerCount ? "Revised answer draft" : "Private answer draft"}
            </h3>
            <span>Only you can see this</span>
          </div>
          <div className="attorney-draft-slot">{draft}</div>
          {answerCount > 0 && (
            <button
              type="button"
              className="attorney-answer-history-toggle"
              onClick={() => setRevising(false)}
            >
              <span>Delivered answers · {answerCount}</span>
              <span>Show</span>
            </button>
          )}
        </>
      ) : (
        <div
          className="attorney-answer-history-content is-primary"
          tabIndex={0}
          aria-label={
            answerCount ? "Delivered answer history" : "Answer record"
          }
        >
          {answerCount > 0 && (
            <div className="attorney-delivered-heading">
              <p className="attorney-delivered-label">
                Delivered to your client · preserved as delivered
              </p>
              {canRevise && !closed ? (
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setRevising(true)}
                >
                  Write a revised answer
                </button>
              ) : (
                <span className="attorney-delivered-label">
                  {closed
                    ? "Closed · read-only"
                    : "Follow-up window ended · no more revisions"}
                </span>
              )}
            </div>
          )}
          {answers}
        </div>
      )}
    </div>
  );
  const workPanel = (
    <section
      className={`attorney-panel attorney-panel-work${focus === "work" ? " is-focused" : ""}`}
      id={`${prefix}-work`}
      aria-label="Your work"
    >
      <header className="attorney-panel-heading attorney-work-tabs">
        <div role="tablist" aria-label="Your work">
          <button
            type="button"
            role="tab"
            id={`${prefix}-tab-answer`}
            aria-selected={tab === "answer"}
            aria-controls={`${prefix}-work-content`}
            onClick={() => onTabChange("answer")}
          >
            Answer
          </button>
          <button
            type="button"
            role="tab"
            id={`${prefix}-tab-messages`}
            aria-selected={tab === "messages"}
            aria-controls={`${prefix}-work-content`}
            onClick={() => onTabChange("messages")}
          >
            Messages
            {messageCount > 0 && (
              <span
                className={`attorney-tab-count${clientWaiting ? " is-waiting" : ""}`}
                aria-label={
                  clientWaiting
                    ? `${messageCount} messages, client waiting for a reply`
                    : `${messageCount} messages`
                }
              >
                {messageCount}
              </span>
            )}
          </button>
        </div>
        <span className="attorney-panel-hint">
          {closed
            ? "Closed · read-only"
            : tab === "answer" && showDraft
              ? "Private until delivered"
              : "Shared with client"}
        </span>
        {expandButton("work", tab === "answer" ? "Answer" : "Messages")}
      </header>
      <div
        id={`${prefix}-work-content`}
        role="tabpanel"
        aria-labelledby={`${prefix}-tab-${tab}`}
        className={`attorney-panel-content attorney-work-content is-${tab}`}
      >
        {/* Both stay mounted so unsent text and scroll positions survive tab changes. */}
        <div hidden={tab !== "answer"} className="attorney-work-pane">
          {answerTab}
        </div>
        <div hidden={tab !== "messages"} className="attorney-work-pane">
          {discussion}
        </div>
      </div>
    </section>
  );
  const recordPanel = (
    <section
      className={`attorney-panel attorney-panel-record${focus === "record" ? " is-focused" : ""}`}
      id={`${prefix}-record`}
      aria-labelledby={`${prefix}-record-heading`}
    >
      <header className="attorney-panel-heading">
        <h2 id={`${prefix}-record-heading`}>Client's materials</h2>
        <span className="attorney-panel-hint">Read-only</span>
        {expandButton("record", "Client's materials")}
      </header>
      <div
        className="attorney-panel-content"
        tabIndex={0}
        aria-label="Client's materials content"
      >
        {record}
      </div>
    </section>
  );
  const split = hasDocuments ? layout.documentRows : layout.columns;
  return (
    <div
      ref={workspace}
      className={`attorney-workspace${hasDocuments ? " with-documents" : " without-documents"}${focus ? " has-focused-panel" : ""}`}
      aria-label="Attorney review workspace"
      style={
        hasDocuments
          ? {
              gridTemplateColumns: "minmax(0, 1fr)",
              gridTemplateRows: `minmax(0, ${split}fr) 10px minmax(0, ${100 - split}fr)`,
            }
          : {
              gridTemplateColumns: `minmax(0, ${split}fr) 10px minmax(0, ${100 - split}fr)`,
              gridTemplateRows: "minmax(0, 1fr)",
            }
      }
      onKeyDown={e => {
        if (e.key === "Escape" && focus) {
          e.stopPropagation();
          focusPanel(null);
        }
      }}
    >
      {recordPanel}
      <Divider
        name={
          hasDocuments
            ? "Resize client's materials and your work"
            : "Resize reading and writing columns"
        }
        value={split}
        range={hasDocuments ? limits.documentRows : limits.columns}
        axis={hasDocuments ? "y" : "x"}
        controls={`${prefix}-record`}
        onChange={value =>
          resize(hasDocuments ? "documentRows" : "columns", value)
        }
      />
      {workPanel}
    </div>
  );
});
export default AttorneyWorkspace;
